import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api';
import { useRef } from 'react';
import { useCallback } from 'react';


const QUIZ_SESSION_DURATION_MS = 30 * 60 * 1000;

const LEARNING_PATH_URL = 'https://sellercentral.amazon.in/gc/seller-university/advanced-learning-path?ref=quiz_page';

const formatTimer = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};


export default function TakeQuiz() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(null);
  const [attemptLimitReached, setAttemptLimitReached] = useState(false);
  const [nextAvailableTime, setNextAvailableTime] = useState('');
  // const [currentSection, setCurrentSection] = useState('');
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [showAlreadyPassedPopup, setShowAlreadyPassedPopup] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(3);

  // Fetch attempts remaining for this quiz
  const getAttemptsRemaining = useCallback(async () => {
    try {
      const res = await API.get('/attempts');
      const userAttempts = res.data[id] || [];
      const attemptsArray = Array.isArray(userAttempts)
        ? userAttempts
        : (typeof userAttempts === 'object' && userAttempts !== null)
        ? [userAttempts]
        : [];
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const recent = attemptsArray.filter(
        a => new Date(a.timestamp || a.date) > twentyFourHoursAgo
      );
      setAttemptsLeft(Math.max(0, 3 - recent.length));
    } catch (err) {
      setAttemptsLeft(3);
    }
  }, [id]);
  const [attemptId, setAttemptId] = useState(null);
  const startCalledRef = useRef(false);
  const timeoutHandledRef = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [attemptExpiresAt, setAttemptExpiresAt] = useState(null);
  const [timeRemainingMs, setTimeRemainingMs] = useState(QUIZ_SESSION_DURATION_MS);
  const [passingMark, setPassingMark] = useState(null);

  const getAttemptsArray = (attemptsData) => {
    const userAttempts = attemptsData?.[id] || [];
    if (Array.isArray(userAttempts)) return userAttempts;
    if (typeof userAttempts === 'object' && userAttempts !== null) return [userAttempts];
    return [];
  };

  const checkHasPassedBefore = async () => {
    try {
      const res = await API.get('/attempts');
      const attemptsArray = getAttemptsArray(res.data);
      const passThreshold = Number(passingMark) || 60;
      const passed = attemptsArray.some((attempt) =>
        attempt?.passed === true ||
        (attempt?.score != null && Number(attempt.score) >= passThreshold)
      );
      return passed;
    } catch (err) {
      return false;
    }
  };


  // const PASSING_MARK = 60;

  // Determine display name: Seller ID (uppercase) if seller, else username
  const sellerId = localStorage.getItem('sellerId');
  const username = localStorage.getItem('username') || 'User';
  const displayName = sellerId ? sellerId.toUpperCase() : username;

  const applyAttemptTiming = useCallback((timing) => {
    const expiresAtMs = timing?.expiresAt
      ? new Date(timing.expiresAt).getTime()
      : Date.now() + QUIZ_SESSION_DURATION_MS;

    setAttemptExpiresAt(expiresAtMs);
    setTimeRemainingMs(
      Number.isFinite(timing?.remainingMs)
        ? Math.max(0, timing.remainingMs)
        : Math.max(0, expiresAtMs - Date.now())
    );
  }, []);

  const logoutForExpiredSession = useCallback(async () => {
    if (timeoutHandledRef.current) return;
    timeoutHandledRef.current = true;
    setSessionExpired(true);

    try {
      await API.get(`/attempts/status/${id}`);
    } catch (err) {
      // The user is being logged out either way.
    }

    localStorage.clear();
    window.location.replace('/seller-login');
  }, [id]);

  const shuffleArray = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

useEffect(() => {
  async function fetchQuiz() {
    const res = await API.get(`/quizzes/${id}`);
    setQuiz(res.data);
    setPassingMark(res.data.passpercentage); 
  }
  fetchQuiz();
}, [id]);


  useEffect(() => {
    API.get(`/quizzes/${id}`)
      .then(res => {
                const original = res.data.questions;

                const sectionMap = {};
                original.forEach(q => {
                  const section = q.section || 'General';
                  if (!sectionMap[section]) sectionMap[section] = [];
                  sectionMap[section].push(q);
                });

                // Shuffle inside each section
                const orderedQuestions = [];
                Object.keys(sectionMap).forEach(section => {
                  orderedQuestions.push(...shuffleArray(sectionMap[section]));
                });

                setQuiz({ ...res.data, questions: orderedQuestions });
              })
      .catch(err => console.error('Failed to load quiz:', err));
  }, [id]);

useEffect(() => {
  if (!quiz || startCalledRef.current) return;

  startCalledRef.current = true;

  const startAttempt = async () => {
    try {
      const res = await API.post('/attempts/start', { quizId: id });
      setAttemptId(res.data.attemptId || null);
      applyAttemptTiming(res.data);
    } catch (err) {
      console.error('Failed to start attempt', err);
    }
  };

  startAttempt();
}, [quiz, id, applyAttemptTiming]);

useEffect(() => {
  if (!attemptExpiresAt || showResult) return;

  const tick = () => {
    const remaining = Math.max(0, attemptExpiresAt - Date.now());
    setTimeRemainingMs(remaining);

    if (remaining <= 0) {
      logoutForExpiredSession();
    }
  };

  tick();
  const timer = setInterval(tick, 1000);
  return () => clearInterval(timer);
}, [attemptExpiresAt, showResult, logoutForExpiredSession]);

useEffect(() => {
  const checkSession = async () => {
    try {
      const res = await API.get(`/attempts/status/${id}`);
      if (res.data.status === 'expired') {
        logoutForExpiredSession();
      } else if (res.data.status === 'active') {
        applyAttemptTiming(res.data);
      }
    } catch {}
  };


  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      checkSession();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  return () => document.removeEventListener('visibilitychange', onVisibility);
}, [id, applyAttemptTiming, logoutForExpiredSession]);


  // Check attempt limit after showing result
  useEffect(() => {
    const checkLimit = async () => {
      try {
        const res = await API.get('/attempts');
        const userAttempts = res.data[id] || [];
        const attemptsArray = Array.isArray(userAttempts)
          ? userAttempts
          : (typeof userAttempts === 'object' && userAttempts !== null)
          ? [userAttempts]
          : [];
        const now = new Date();
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const recent = attemptsArray.filter(
          a => new Date(a.timestamp || a.date) > twentyFourHoursAgo
        );
        setAttemptsLeft(Math.max(0, 3 - recent.length));
        if (recent.length >= 3) {
          const oldestTimestamp = Math.min(
            ...recent.map(a => new Date(a.timestamp || a.date).getTime())
          );
          const next = new Date(oldestTimestamp + 24 * 60 * 60 * 1000);
          setAttemptLimitReached(true);
          setNextAvailableTime(next.toLocaleString());
        }
      } catch (err) {
        setAttemptsLeft(3);
        console.error('Error checking attempt limit:', err);
      }
    };

    if (showResult && quiz) {
      checkLimit();
    } else if (quiz) {
      getAttemptsRemaining();
    }
  }, [showResult, id, quiz, getAttemptsRemaining]);

  const currentQuestion = quiz?.questions[currentQuestionIndex];



  const handleChange = (value) => {
    setAnswers({ ...answers, [currentQuestionIndex]: value });
  };

  const handleMultiSelect = (option, checked) => {
    const current = answers[currentQuestionIndex] || [];
    if (checked) {
      setAnswers({ ...answers, [currentQuestionIndex]: [...current, option] });
    } else {
      setAnswers({
        ...answers,
        [currentQuestionIndex]: current.filter(v => v !== option)
      });
    }
  };

  const goNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    quiz.questions.forEach((q, idx) => {
      const userAns = answers[idx];
      const correctAns = q.correct;

      if (q.type === 'multi_select') {
        const userSorted = (userAns || []).sort();
        const correctSorted = Array.isArray(correctAns)
          ? correctAns.sort()
          : correctAns.split(',').map(c => c.trim()).sort();
        if (JSON.stringify(userSorted) === JSON.stringify(correctSorted)) correct++;
      } else if (String(userAns)?.trim() === String(correctAns)?.trim()) {
        correct++;
      }
    });

    const percentage = quiz.questions.length > 0
      ? (correct / quiz.questions.length) * 100
      : 0;
    return percentage.toFixed(2);
  };

  const submitQuiz = async () => {
  if (Object.keys(answers).length < totalQuestions) {
    // setSubmitClicked(true);
    return;
  } 

    const passedAlready = await checkHasPassedBefore();
    if (passedAlready) {
      // Cleanup: delete all attempts after the first passed attempt
      try {
        await API.post('/attempts/cleanup', { quizId: id });
      } catch (err) {
        console.error('Cleanup failed:', err);
      }
      setShowAlreadyPassedPopup(true);
      return;
    }

    // attemptsLeft > 0 && setAttemptsLeft(attemptsLeft - 1);
    const finalScore = calculateScore();
    setScore(finalScore);
    setShowResult(true);

    try {
      await API.post('/attempts', { quizId: id, score: finalScore,attemptId });
    } catch (err) {
          if (err.response?.status === 410) {
      await logoutForExpiredSession();
      return;
    }
      console.error('Failed to save attempt:', err);
      // Optionally show user feedback
    }

  };

  const reattemptQuiz = async () => {
    const res = await API.post('/attempts/start', { quizId: id });
    setAttemptId(res.data.attemptId || null);
    timeoutHandledRef.current = false;
    applyAttemptTiming(res.data);
    setAnswers({});
    setCurrentQuestionIndex(0);
    setScore(null);
    setShowResult(false);
    setAttemptLimitReached(false);
  };

  const [paletteOpen, setPaletteOpen] = useState(false);

  const attemptedCount = Object.keys(answers).length;
  const totalQuestions = quiz?.questions.length || 0;

  // Shared header for all quiz views
  const QuizHeader = () => (
    <header className="amz-header">
      <img src="/scaling_white_background.png" alt="Amazon" className="amz-header-logo" />
      <div className="amz-header-welcome">
        Welcome,&nbsp;<b>{displayName}</b>
      </div>
      <button
        className="amz-logout"
        onClick={() => { localStorage.clear(); window.location.href = '/seller-login'; }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Logout
      </button>
    </header>
  );

  // SESSION EXPIRED
  if (sessionExpired) {
    return (
      <div className="tq-page">
        <QuizHeader />
        <div className="tq-result-layout">
          <div className="tq-result-card expired">
            <div className="tq-result-icon expired">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="tq-result-title">Session Expired</h2>
            <p className="tq-result-msg fail">
              Your quiz session has expired due to inactivity.<br />
              Please re-attempt the quiz from your dashboard.
            </p>
            <div className="tq-result-actions">
              <button className="amz-btn amz-btn-primary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LOADING
  if (!quiz) {
    return (
      <div className="tq-page">
        <QuizHeader />
        <div className="tq-result-layout">
          <div className="tq-result-card">
            <div className="tq-result-icon expired" style={{ animation: 'pulse 1.5s infinite' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="tq-result-title">Loading quiz...</h2>
          </div>
        </div>
      </div>
    );
  }

  // RESULT SCREEN — split layout
  if (showResult) {
    const passed = parseFloat(score) >= Number(passingMark);
    const circumference = 2 * Math.PI * 72; // r=72

    return (
      <div className="tq-page">
        <QuizHeader />
        <div className="tq-result-split">
          {/* Left colored panel with ring */}
          <div className={`tq-result-left ${passed ? 'pass' : 'fail'}`}>
            <div className="tq-result-ring">
              <svg viewBox="0 0 160 160">
                <circle className="tq-result-ring-bg" cx="80" cy="80" r="72" />
                <circle
                  className="tq-result-ring-progress"
                  cx="80" cy="80" r="72"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference - (circumference * parseFloat(score)) / 100}
                />
              </svg>
              <div className="tq-result-ring-center">
                <div className="tq-result-ring-value">{score}%</div>
                <div className="tq-result-ring-label">Your Score</div>
              </div>
            </div>
            <div className="tq-result-status">
              <div className="tq-result-status-icon">
                {passed ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              {passed ? 'Congratulations!' : 'Better luck next time!'}
            </div>
          </div>

          {/* Right details panel */}
          <div className="tq-result-right">
            <div className="tq-result-right-inner">
              <h2>{passed ? "Congratulations!" : "Keep Trying"}</h2>
              <p className="tq-result-subtitle">
                {passed
                  ? "You've successfully passed the assessment"
                  : "You didn't reach the passing score this time"}
              </p>

              {passed && (
                <div className="tq-result-info pass">
                  To claim credits, please complete a minimum of 4 videos in this learning path. If you have already completed the required videos, you will receive an email with the steps to avail the credits.
                </div>
              )}

              {!passed && !attemptLimitReached && (
                <div className="tq-result-info fail">
                  You have <strong>{attemptsLeft}</strong> attempt(s) remaining.<br />
                  We recommend revisiting the learning path videos before retrying.
                </div>
              )}

              {!passed && attemptLimitReached && (
                <div className="tq-result-info fail">
                  You've used all 3 attempts for today.<br />
                  You can retake this Quiz after<br />
                  <strong>{nextAvailableTime}</strong>
                </div>
              )}

              <div className="tq-result-actions">
                {!passed && !attemptLimitReached && (
                  <button className="amz-btn amz-btn-primary amz-btn-lg" onClick={reattemptQuiz}>
                    Re-attempt Assessment
                  </button>
                )}
                {!passed && (
                  <a
                    href={LEARNING_PATH_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="amz-btn amz-btn-secondary amz-btn-lg"
                  >
                    Watch learning path videos
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 12l8-8M7 4h5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                )}
                <button className="amz-btn amz-btn-secondary amz-btn-lg" onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // QUIZ INTERFACE — with slide-out question palette
  return (
    <div className="tq-page">
      <QuizHeader />

      {/* Sticky info bar */}
      <div className="tq-info-bar">
        <div className="tq-info-bar-inner">
          <span className="tq-info-title">{quiz.title}</span>
          <div className="tq-info-progress">
            <div
              className="tq-info-progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
          <span className="tq-info-counter">
            {Object.keys(answers).length}/{totalQuestions} answered
          </span>
          <span className={`tq-info-timer${timeRemainingMs <= 5 * 60 * 1000 ? ' warning' : ''}`}>
            <span className="tq-info-timer-label">Time left</span>
            <b>{formatTimer(timeRemainingMs)}</b>
          </span>
        </div>
      </div>

      {/* Layout wrapper for question + palette */}
      <div className={`tq-layout${paletteOpen ? ' palette-open' : ''}`}>
        {/* Central question */}
        <div className="tq-center">
          <div className="tq-question-card">
            <div className="tq-question-header">
              <div className="tq-question-number">{currentQuestionIndex + 1}</div>
              <div className="tq-question-meta">
                <span className="tq-question-counter">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </span>
                <span className="tq-question-type">
                  {currentQuestion.type === 'single_select' ? 'Single Choice' :
                   currentQuestion.type === 'multi_select' ? 'Multiple Choice' : 'Text Answer'}
                </span>
              </div>
            </div>

            <div className="tq-question-text">{currentQuestion.text}</div>
            {currentQuestion.type === 'multi_select' && (
              <p className="tq-multi-select-note">Select all options that are applicable.</p>
            )}

            <div className="tq-options">
              {currentQuestion.type === 'single_select' &&
                currentQuestion.options.map((opt, idx) => (
                  <label key={idx} className="tq-option">
                    <input
                      type="radio"
                      checked={answers[currentQuestionIndex] === opt}
                      onChange={() => handleChange(opt)}
                    />
                    <span className="tq-option-letter">{String.fromCharCode(65 + idx)}</span>
                    {opt}
                  </label>
                ))}

              {currentQuestion.type === 'multi_select' &&
                currentQuestion.options.map((opt, idx) => (
                  <label key={idx} className="tq-option">
                    <input
                      type="checkbox"
                      checked={(answers[currentQuestionIndex] || []).includes(opt)}
                      onChange={(e) => handleMultiSelect(opt, e.target.checked)}
                    />
                    <span className="tq-option-letter">{String.fromCharCode(65 + idx)}</span>
                    {opt}
                  </label>
                ))}

              {['text', 'integer'].includes(currentQuestion.type) && (
                <textarea
                  className="tq-textarea"
                  placeholder="Type your answer here..."
                  value={answers[currentQuestionIndex] || ''}
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            </div>

            {/* Inline prev/next buttons under the question */}
            <div className="tq-nav-row">
              <button
                className="amz-btn amz-btn-secondary"
                disabled={currentQuestionIndex === 0}
                onClick={goPrev}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M13 8H3M7 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous
              </button>

              {currentQuestionIndex === totalQuestions - 1 ? (
                <button
                  className="amz-btn amz-btn-success"
                  onClick={() => {
                    if (Object.keys(answers).length < totalQuestions) {
                      setShowSubmitWarning(true);
                    } else {
                      submitQuiz();
                    }
                  }}
                >
                  Submit
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <button className="amz-btn amz-btn-primary" onClick={goNext}>
                  Next
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Question palette — slide-out panel */}
        <div className={`tq-palette${paletteOpen ? ' open' : ''}`}>
          <div className="tq-palette-header">
            <h4>Question Navigator</h4>
            <button className="tq-palette-close" onClick={() => setPaletteOpen(false)} aria-label="Close palette">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="tq-palette-stats">
            <div className="tq-palette-stat">
              <div className="tq-palette-stat-ring">
                <svg viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E7E7E7" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--amz-success)" strokeWidth="3"
                    strokeDasharray={2 * Math.PI * 15.5}
                    strokeDashoffset={2 * Math.PI * 15.5 - (2 * Math.PI * 15.5 * attemptedCount / Math.max(totalQuestions, 1))}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset .4s ease' }}
                  />
                </svg>
                <span>{attemptedCount}</span>
              </div>
              <label>Answered</label>
            </div>
            <div className="tq-palette-stat">
              <div className="tq-palette-stat-ring">
                <svg viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E7E7E7" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--amz-orange)" strokeWidth="3"
                    strokeDasharray={2 * Math.PI * 15.5}
                    strokeDashoffset={2 * Math.PI * 15.5 - (2 * Math.PI * 15.5 * (totalQuestions - attemptedCount) / Math.max(totalQuestions, 1))}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset .4s ease' }}
                  />
                </svg>
                <span>{totalQuestions - attemptedCount}</span>
              </div>
              <label>Remaining</label>
            </div>
          </div>

          <div className="tq-palette-legend">
            <span className="tq-legend-item"><span className="tq-legend-dot current" />Current</span>
            <span className="tq-legend-item"><span className="tq-legend-dot answered" />Answered</span>
            <span className="tq-legend-item"><span className="tq-legend-dot unanswered" />Not Answered</span>
          </div>

          <div className="tq-palette-grid">
            {Array.from({ length: totalQuestions }).map((_, i) => {
              let cls = 'tq-palette-btn';
              if (i === currentQuestionIndex) cls += ' current';
              else if (answers[i] !== undefined) cls += ' answered';
              return (
                <button
                  key={i}
                  className={cls}
                  onClick={() => { setCurrentQuestionIndex(i); setPaletteOpen(false); }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {currentQuestionIndex === totalQuestions - 1 && (
            <button
              className="amz-btn amz-btn-success tq-palette-submit"
              onClick={() => {
                if (Object.keys(answers).length < totalQuestions) {
                  setShowSubmitWarning(true);
                } else {
                  submitQuiz();
                }
              }}
            >
              Submit Assessment
            </button>
          )}
        </div>
      </div>

      {/* Floating palette toggle button */}
      <button
        className={`tq-palette-fab${paletteOpen ? ' active' : ''}`}
        onClick={() => setPaletteOpen(!paletteOpen)}
        aria-label="Toggle question navigator"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
        <span className="tq-fab-badge">{attemptedCount}/{totalQuestions}</span>
      </button>

      {/* Backdrop for mobile when palette is open */}
      {paletteOpen && <div className="tq-palette-backdrop" onClick={() => setPaletteOpen(false)} />}

      {/* Modals */}
      {showSubmitWarning && (
        <div className="tq-modal-overlay">
          <div className="tq-modal">
            <h3>Incomplete Submission</h3>
            <p>
              Please attempt <strong>all questions</strong> before submitting the assessment.
            </p>
            <button className="amz-btn amz-btn-primary" onClick={() => setShowSubmitWarning(false)}>
              OK, Continue Quiz
            </button>
          </div>
        </div>
      )}

      {showAlreadyPassedPopup && (
        <div className="tq-modal-overlay">
          <div className="tq-modal">
            <h3>Already Passed</h3>
            <p>
              Congratulations! You have already passed this quiz.<br />
              Returning to the dashboard to continue your journey.
            </p>
            <button className="amz-btn amz-btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
